import { LeaderboardRecordCategory, QueryKey } from '@/constants/shared';
import { makeRequest } from '@/network/api';
import { LeaderboardRecord } from '@/types';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

type LeaderboardPage = {
  leaderboard: LeaderboardRecord[];
  day1Override?: {
    userRank: number;
    userXp: number;
  };
  groupSize?: number;
  groupRemaining?: number;
  battleGroup?: number;
};

const PAGE_SIZE = 20;

// adding in the walletAddress param to allow for fetching data for another user (backend blocks if not admin)
export const useLeaderboardData = (
  recordCategory: LeaderboardRecordCategory,
  walletAddress?: string | null,
  overrideDay1Override?: boolean,
  doLeaderboardPreview?: boolean,
) => {
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<
    LeaderboardPage, // TQueryFnData
    Error, // TError
    InfiniteData<LeaderboardPage>, // TData
    [string, LeaderboardRecordCategory, string], // TQueryKey
    number // TPageParam
  >({
    queryKey: [QueryKey.Leaderboard, recordCategory, walletAddress || 'none'],
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.leaderboard?.length < PAGE_SIZE ? undefined : allPages.length + 1,
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }): Promise<LeaderboardPage> => {
      const offset = (pageParam - 1) * PAGE_SIZE;
      const response = await makeRequest(
        `stats/${recordCategory}?offset=${offset}&count=${PAGE_SIZE}&walletAddress=${walletAddress}&overrideDay1Override=${overrideDay1Override}&preview=${doLeaderboardPreview}`,
      );
      const responseJson = await response.json();
      return responseJson.data;
    },
  });

  const leaderboard = useMemo(
    () =>
      data?.pages?.reduce(
        (allRecords, nextPage) => allRecords.concat(nextPage?.leaderboard ?? []),
        [] as LeaderboardRecord[],
      ),
    [data],
  );

  const userOverride = useMemo(() => data?.pages?.[0]?.day1Override, [data]);

  const latestRecord = useMemo(() => data?.pages?.at(-1), [data]);

  return {
    latestRecord,
    leaderboard,
    isLoadingLeaderboard: isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    userOverride,
  };
};
